import { IS_SERVER } from '@/renderer/utils/ssr';

/**
 * Shared home-screen app-icon badge aggregator (issue #1347).
 *
 * The badge (via the Badging API `navigator.setAppBadge` / `clearAppBadge`) is a
 * single OS-level number, but we have multiple independent unread sources feeding
 * it — support-chat unreads and release-center unreads. If each source wrote the
 * badge directly they'd clobber each other (whichever updated last would win),
 * which is why support-chat notifications were getting masked by the release
 * center count and going unseen.
 *
 * Every unread source reports its current count here keyed by a stable id. The
 * badge is then set to the SUM of all sources, and only cleared once every source
 * is at zero. The Badging API is feature-detected and PWA/installed-only, so this
 * is a no-op (never throws) on browsers/contexts that don't support it.
 */

const counts = new Map<string, number>();

/**
 * The app-icon badge is a SINGLE per-app value that ANY same-origin context can
 * write — including the notifications service worker (sw/sw.ts), which sets it
 * from a background Web Push so a CLOSED or BACKGROUNDED PWA still badges when a
 * support-chat message arrives (#1694/#1719). A page that is NOT visible must
 * therefore never write the badge: if it did, a backgrounded tab / minimized PWA
 * window would clobber the SW's push-set badge back to its own (usually zero)
 * live unread total — the exact "the notification arrives but no badge appears"
 * bug. So the page owns the badge only while it is the visible one (reconciling
 * to the true unread total then, including clearing once everything is read), and
 * leaves it to the service worker while hidden. This mirrors, from the page side,
 * the SW's own "skip if a focused client exists" push guard.
 */
function pageOwnsBadge(): boolean {
  return !IS_SERVER && typeof document !== 'undefined' && document.visibilityState === 'visible';
}

function applyBadge() {
  // Badging API only exists in secure, installed/PWA contexts. Guard it so we
  // never throw where it's unsupported (plain browser tabs, SSR, etc.).
  if (IS_SERVER || typeof navigator === 'undefined' || !('setAppBadge' in navigator)) {
    return;
  }

  // Don't let a hidden page fight the service worker's push-set badge (#1719).
  if (!pageOwnsBadge()) {
    return;
  }

  let total = 0;
  for (const value of counts.values()) {
    total += value;
  }

  if (total <= 0) {
    navigator.clearAppBadge?.();
  } else {
    navigator.setAppBadge?.(total);
  }
}

/** Report the current unread count for a given source and refresh the badge. */
export function setBadgeSource(key: string, count: number) {
  counts.set(key, Math.max(count, 0));
  applyBadge();
}

/** Remove a source (e.g. on unmount) and refresh the badge from the remainder. */
export function clearBadgeSource(key: string) {
  if (counts.delete(key)) {
    applyBadge();
  }
}

// Reconcile when this page becomes visible again — the moment it takes badge
// ownership back from the service worker. Without this, a page that was hidden
// (its live counts kept, the SW owning the badge) wouldn't refresh the badge to
// its own totals until the next count change. Registered once at module load;
// no-op on the server / where there's no document.
if (!IS_SERVER && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      applyBadge();
    }
  });
}
